import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  clientMasterApi,
} from '@/services/api';
import FeeStructure from './FeeStructure';
import Contributions from './Contributions';
import FeeNotes from './FeeNotes';
import styles from './ClientForm.module.css';
import commonStyles from './ClientCommon.module.css';
import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
} from '@mui/material';
import { PageFrame, PageHeader, Surface, Button, TabBar, FilterAsyncMultiSelect, Note, Placeholder, confirmColors } from '@/components/ui';
import { ClientInfoSection, getClientFieldConfigs } from './ClientCommon';
import ClientSeriesField from './ClientSeriesField';
import { toast } from 'react-toastify';
import ScopeGate from '@/components/utils/ScopeGate';
import { READ_CLIENT_MASTER, WRITE_CLIENT_MASTER } from '@/constants/scopeCodes';
import ApprovalActions from './ApprovalActions';
import Swal from 'sweetalert2';
import { useNavigate, useParams } from "react-router-dom";
import { useClientForm } from './hooks/useClientForm';
import { useClientSupportingData } from './hooks/useClientSupportingData';
import { AIF_FIRM_TYPE, IFSC_AIF_FIRM_TYPE } from '@/types/client';
import { describeRequestError } from '@/utils/apiErrors';
import { normalizeEmailList } from '@/utils/validators';
import { useApprovalReviewLogic } from './hooks/useApprovalReviewLogic';
import {
  REASON_LABEL_STYLE,
  REASON_REQUIRED_MARK,
  REASON_SELECT_STYLE,
  REASON_TEXTAREA_STYLE,
} from './utils/changeReasonStyles';

interface ClientFormProps {
  clientType?: 'PMS' | 'AIF';
  viewMode?: boolean;
  approvalId?: string;
}

function extractCompanyOptions(
  companies: unknown,
): Array<{ id: number; label: string; name: string; symbol: string }> {
  if (!Array.isArray(companies)) return [];
  return companies
    .filter((c): c is { id: number | string; name: string; symbol: string } =>
      c != null && typeof c === 'object' && 'id' in c && c.id != null,
    )
    .map((c) => ({
      id: Number(c.id),
      label: `${c.name} (${c.symbol})`,
      name: c.name,
      symbol: c.symbol,
    }));
}

const ClientForm: React.FC<ClientFormProps> = ({
  clientType = 'PMS',
  viewMode = false,
  approvalId,
}) => {
  useAuth();
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Derived initial client ID from route
  const initialClientId = routeId && !approvalId ? parseInt(routeId) : null;
  const [clientId, setClientId] = useState<number | null>(initialClientId);

  // Keep clientId in sync with the URL param. React may reuse this component
  // instance when navigating between routes (e.g. /new → /:id after creation),
  // so useState(initialClientId) alone won't reflect the new route's id.
  // Skip in approval mode — clientId there is resolved async from the approval
  // payload, not from the URL param (which is the approval ID, not client ID).
  useEffect(() => {
    if (approvalId) return;
    const newClientId = routeId ? parseInt(routeId) : null;
    if (newClientId !== clientId) {
      setClientId(newClientId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  const [isTypeSelected, setIsTypeSelected] = useState<boolean>(!!clientId || !!approvalId);
  const [activeTab, setActiveTab] = useState<'info' | 'fees'>('info');
  // Fee Structure & Contributions tab is mounted lazily on first visit so its
  // API calls (fee-components, contributions, fee-notes, metadata) don't fire
  // on initial page load. Once visited it stays mounted (display toggle) so
  // pending edits and save refs survive tab switches.
  // IMPORTANT: keep the {feeTabVisited && ...} guard in the JSX below. Removing
  // it re-introduces eager loading; the display:none path alone does not prevent
  // child components from firing their useEffect API calls on mount.
  const [feeTabVisited, setFeeTabVisited] = useState(false);
  // null until the user edits Series, so an untouched (or unloaded) picker never sends a list.
  const [seriesRefs, setSeriesRefs] = useState<string[] | null>(null);
  const [isSyncingToZoho, setIsSyncingToZoho] = useState(false);

  const {
    formData,
    setFormData,
    validationErrors,
    loading: formSaving,
    setLoading: setFormSaving,
    dataLoading: formDataLoading,
    approvalDetails,
    originalClientData,
    changedFields,
    selectedClientType: formClientType,
    setSelectedClientType: setFormClientType,
    handleInputChange,
    validateForm,
    fetchClient,
    fetchApproval,
    setRequiredFields,
    setValidationErrors
  } = useClientForm({
    clientId,
    approvalId,
    clientType: clientType,
    requiredFields: []
  });

  const {
    schemes,
    categories,
    groups,
    clientClasses,
    distributors,
    countries,
    companyOptions,
    setCompanyOptions,
    fetchCompanies,
    requiredFields,
    fieldConfigs,
    dataLoading: supportingDataLoading,
    error: supportingDataError
  } = useClientSupportingData(formClientType);

  // Seed the select with the labels of already-selected restricted companies.
  // The client detail payload returns them as {id, name, symbol}, so chips for
  // companies absent from the active options list (e.g. since deactivated) still render.
  useEffect(() => {
    const seeded = [
      ...extractCompanyOptions(originalClientData?.restricted_companies),
      ...extractCompanyOptions(approvalDetails?.object_details?.restricted_companies),
      ...extractCompanyOptions(approvalDetails?.data?.clientmaster?.restricted_companies),
    ];

    if (seeded.length === 0) return;

    setCompanyOptions((prev) => {
      const optionById = new Map(prev.map((option) => [option.id, option]));
      seeded.forEach((option) => {
        if (!optionById.has(option.id)) optionById.set(option.id, option);
      });
      return Array.from(optionById.values());
    });
  }, [originalClientData, approvalDetails, setCompanyOptions]);

  // --- Memoized Options for Restricted Companies ---
  const mappedCompanyOptions = useMemo(() =>
    companyOptions.map(opt => ({
      value: opt.id.toString(),
      label: opt.label,
      id: opt.id
    })),
    [companyOptions]
  );

  const selectedCompanyOptions = useMemo(() => {
    const selectedIds = formData.restricted_companies || [];

    // Filter and map in one pass for efficiency
    return mappedCompanyOptions.filter(opt => {
      // Ensure we're comparing numbers with numbers
      return selectedIds.includes(opt.id);
    });
  }, [mappedCompanyOptions, formData.restricted_companies]);




  const isApprovalMode = useMemo(() => !!approvalId, [approvalId]);

  const { renderPreviousValue } = useApprovalReviewLogic({
    isApprovalMode,
    changedFields,
    originalClientData,
    schemes,
    categories,
    groups,
    clientClasses,
    distributors,
    countries,
    companies: companyOptions,
  });

  // Sync client ID if it changes (e.g. from approval data load)
  useEffect(() => {
    if (formData.id && formData.id !== clientId) {
      setClientId(formData.id);
    }
  }, [formData.id]);

  // New clients default to tax applicable, except on AIF / IFSC AIF schemes; editing keeps the saved value.
  useEffect(() => {
    if (clientId || approvalId) return;
    const firmType = schemes.find(s => s.id === Number(formData.scheme))?.strategy_firm_type;
    const isTaxApplicable = firmType !== AIF_FIRM_TYPE && firmType !== IFSC_AIF_FIRM_TYPE;
    setFormData(prev => ({ ...prev, is_tax_applicable: isTaxApplicable }));
  }, [clientId, approvalId, formData.scheme, schemes, setFormData]);


  // Initial Data Fetch
  useEffect(() => {
    const controller = new AbortController();
    const loadData = async () => {
      if (approvalId) {
        await fetchApproval(parseInt(approvalId), controller.signal);
      } else if (clientId) {
        await fetchClient(clientId, controller.signal);
      }
    };

    loadData();
    return () => controller.abort();
  }, [clientId, approvalId, fetchClient, fetchApproval]);


  const feeStructureRef = useRef<any>(null);
  const contributionsRef = useRef<any>(null);

  const isFieldRequired = useCallback((fieldName: string): boolean => {
    return requiredFields.includes(fieldName);
  }, [requiredFields]);

  // Force push, unlike the nightly cron which only sends clients whose data changed.
  const handleSyncToZoho = useCallback(async () => {
    if (!clientId) return;
    setIsSyncingToZoho(true);
    try {
      const result = await clientMasterApi.syncClientToZoho(clientId);
      if (result.outcome === 'synced') {
        toast.success(`Synced ${result.client_code} to Zoho CRM.`);
      } else if (result.outcome === 'awaiting_conversion') {
        toast.info(
          `${result.client_code} has no Zoho Contact yet — its lead must be converted ` +
          'with this FA account number before it can sync.'
        );
      } else {
        toast.error(`Zoho rejected the sync for ${result.client_code}.`);
      }
    } catch (error) {
      toast.error(describeRequestError(error, 'sync to Zoho CRM'));
    } finally {
      setIsSyncingToZoho(false);
    }
  }, [clientId]);

  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      toast.error('Please fill in the required fields and fix any errors in the form.');
      return;
    }

    try {
      setFormSaving(true);

      const payload: any = {
        ...formData,
        id: clientId,
        // Comma-separated recipient lists: strip stray spaces around each address
        // before saving, rather than storing whatever spacing the user typed.
        email: formData.email ? normalizeEmailList(formData.email) : formData.email,
        relationship_manager_email: formData.relationship_manager_email
          ? normalizeEmailList(formData.relationship_manager_email)
          : formData.relationship_manager_email,
        scheme_id: Number(formData.scheme) || null,
        category_id: Number(formData.category) || null,
        distributor_id: Number(formData.distributor) || null,
        group_id: Number(formData.group) || null,
        client_class_id: formData.client_type === 'AIF' ? (Number(formData.client_class) || null) : null,
        country_id: Number(formData.country_id) || null,
        restricted_companies: formData.restricted_companies || [],
        client_type: formData.client_type,
        block_amount: Number(formData.block_amount) || 0,
        cash_threshold_limit: formData.cash_threshold_limit ?? 0,
        agreement_date: formData.agreement_date || null,
        maturity_date: formData.maturity_date || null,
        performance_reporting_date: formData.performance_reporting_date || null,
        mode_of_holding: formData.mode_of_holding || null,
      };

      // Only include is_active for new client creation
      if (!clientId) {
        payload.is_active = formData.is_active === true;
      }

      // Clean up fields that should not be in the root payload or are mapped to _id
      delete payload.scheme;
      delete payload.category;
      delete payload.distributor;
      delete payload.group;
      delete payload.client_class;
      delete payload.country;
      delete payload.id;
      if (seriesRefs !== null) payload.series = seriesRefs;

      // Check if critical fields changed — require change_reason
      if (clientId && originalClientData) {
        const decimalCriticalFields: Record<string, string> = {
          block_amount: 'Block Amount',
          cash_threshold_limit: 'Cash Threshold Limit',
        };
        const booleanCriticalFields: Record<string, string> = {
          stop_purchase: 'Stop Purchase',
          redemption_only: 'Redemption Only',
          is_derivative_consent: 'Derivative Consent',
          clearing_cash_enable: 'Clearing Cash Enabled',
        };
        const changedCriticalLabels: string[] = [];

        for (const [field, label] of Object.entries(decimalCriticalFields)) {
          const oldVal = parseFloat(String(originalClientData[field] ?? 0)) || 0;
          const newVal = parseFloat(String(formData[field as keyof typeof formData] ?? 0)) || 0;
          if (oldVal !== newVal) changedCriticalLabels.push(label);
        }
        for (const [field, label] of Object.entries(booleanCriticalFields)) {
          const oldVal = Boolean(originalClientData[field]);
          const newVal = Boolean(formData[field as keyof typeof formData]);
          if (oldVal !== newVal) changedCriticalLabels.push(label);
        }

        if (changedCriticalLabels.length > 0) {
          const boldFields = changedCriticalLabels.map(l => `<b>${l}</b>`).join(' and ');

          // Check if stop_purchase specifically changed
          const stopPurchaseChanged =
            Boolean(originalClientData.stop_purchase) !== Boolean(formData.stop_purchase);

          const dropdownOptions = stopPurchaseChanged
            ? ['Fees Collection (Low Cash)', 'Scheme Transition', 'Other']
            : [];

          const reasonResult = await Swal.fire({
            title: 'Reason for Change',
            html: `<div style="text-align:left;overflow:hidden;">
              <p style="margin:0 0 12px;">${boldFields} has been modified. Please provide a reason.</p>` +
              (stopPurchaseChanged
                ? `<label style="${REASON_LABEL_STYLE}">Reason ${REASON_REQUIRED_MARK}</label>
                   <select id="swal-reason-dropdown" style="${REASON_SELECT_STYLE}">
                    <option value="">-- Select a reason --</option>
                    ${dropdownOptions.map(o => `<option value="${o}">${o}</option>`).join('')}
                   </select>`
                : '') +
              `<label style="${REASON_LABEL_STYLE}">${stopPurchaseChanged ? 'Additional Notes' : `Reason ${REASON_REQUIRED_MARK}`}</label>
               <textarea id="swal-reason-notes" rows="3" placeholder="Enter ${stopPurchaseChanged ? 'additional notes' : 'reason for change'}..." style="${REASON_TEXTAREA_STYLE}"></textarea>
            </div>`,
            showCancelButton: true,
            confirmButtonText: 'Submit',
            preConfirm: () => {
              const dropdown = document.getElementById('swal-reason-dropdown') as HTMLSelectElement | null;
              const notes = (document.getElementById('swal-reason-notes') as HTMLTextAreaElement)?.value?.trim() || '';
              const selectedReason = dropdown?.value || '';

              if (stopPurchaseChanged && !selectedReason) {
                Swal.showValidationMessage('Please select a reason from the dropdown');
                return false;
              }
              if (stopPurchaseChanged && selectedReason === 'Other' && !notes) {
                Swal.showValidationMessage('Please provide details when selecting "Other"');
                return false;
              }
              if (!stopPurchaseChanged && !notes) {
                Swal.showValidationMessage('A reason is required');
                return false;
              }

              if (selectedReason && notes) {
                return `${selectedReason} - ${notes}`;
              }
              return selectedReason || notes;
            },
          });

          if (!reasonResult.isConfirmed || !reasonResult.value) {
            setFormSaving(false);
            return;
          }
          payload.change_reason = reasonResult.value;
        }
      }

      let savedClientId = clientId;

      if (clientId) {
        await clientMasterApi.updateClient(clientId, payload);
        toast.success('Client updated successfully');
      } else {
        const newClient = await clientMasterApi.createClient(payload);
        savedClientId = newClient.id;
        toast.success('Client created successfully');
      }

      // Save Fee Structure changes
      const feeChanges = feeStructureRef.current?.getPendingChanges();
      if (feeChanges && savedClientId) {
        if (feeChanges.id) {
          await clientMasterApi.updateFeeComponent(savedClientId, feeChanges);
        } else {
          // Two-step save: backend cannot create nested fee_structures in one call
          const { fee_structures, ...feeComponentData } = feeChanges;
          const savedFeeComponent = await clientMasterApi.addFeeComponent(savedClientId, {
            ...feeComponentData,
            scheme: feeComponentData.scheme || Number(formData.scheme),
            fee_structures: [],
          });
          if (fee_structures && fee_structures.length > 0) {
            const structuresWithCorrectId = fee_structures.map((fs: any) => ({
              ...fs,
              id: undefined,
              fee_component: savedFeeComponent.id,
            }));
            await clientMasterApi.updateFeeComponent(savedClientId, {
              ...savedFeeComponent,
              fee_structures: structuresWithCorrectId,
            });
          }
        }
      }

      // Save Contributions changes
      const contribChanges = contributionsRef.current?.getPendingChanges();
      const deletedContribs = contributionsRef.current?.getDeletedContributions();

      if (savedClientId) {
        // First: Handle Deletions
        if (deletedContribs?.length) {
          for (const contribId of deletedContribs) {
            await clientMasterApi.deleteContribution(savedClientId, contribId);
          }
        }

        // Second: Handle Additions/Updates
        if (contribChanges?.length) {
          for (const contrib of contribChanges) {
            if (contrib.id) {
              await clientMasterApi.updateContribution(savedClientId, contrib.id, contrib);
            } else {
              await clientMasterApi.addContribution(savedClientId, contrib);
            }
          }
        }
      }

      if (savedClientId) {
        const path = `/client-master/${savedClientId}`;
        navigate(path);
      } else {
        navigate('/clients');
      }
    } catch (err: any) {
      console.error(err);
      if (err.response && err.response.data) {
        const backendErrors = err.response.data;
        const newValidationErrors: Record<string, string> = {};
        const nonFieldErrors: string[] = [];

        Object.keys(backendErrors).forEach(key => {
          const errorValue = backendErrors[key];
          const errorMessage = Array.isArray(errorValue) ? errorValue.join(' ') : String(errorValue);

          if (key === 'non_field_errors' || key === 'detail') {
            nonFieldErrors.push(errorMessage);
          } else {
            // Map backend field names to frontend field names
            let fieldName = key;
            if (fieldName === 'scheme_id') fieldName = 'scheme';
            if (fieldName === 'category_id') fieldName = 'category';
            if (fieldName === 'distributor_id') fieldName = 'distributor';
            if (fieldName === 'group_id') fieldName = 'group';
            if (fieldName === 'client_class_id') fieldName = 'client_class';

            newValidationErrors[fieldName] = errorMessage;
          }
        });

        if (Object.keys(newValidationErrors).length > 0) {
          setValidationErrors(prev => ({ ...prev, ...newValidationErrors }));
        }

        if (nonFieldErrors.length > 0) {
          toast.error(nonFieldErrors.join('. '));
        } else if (Object.keys(newValidationErrors).length === 0) {
          toast.error('Failed to save client: ' + (err.message || 'Unknown error'));
        }

      } else {
        toast.error('Failed to save client');
      }
    } finally {
      setFormSaving(false);
    }
  }, [formData, clientId, seriesRefs, validateForm, navigate, setFormSaving, setValidationErrors]);

  const handleTradingSettingsChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    try {
      const { name, value, type, checked } = e.target as any;
      const newValue = type === 'checkbox' ? checked : value;

      if (name === 'redemption_only' && newValue === true) {
        const result = await Swal.fire({
          title: 'Enable Redemption Only?',
          icon: 'warning',
          html: `<p style="text-align:left;margin:0;">Enabling <b>Redemption Only</b> for this client means only redemption (sell) orders will be placed for them. All buy orders and non-redemption sell orders will be excluded for this client.</p>`,
          showCancelButton: true,
          confirmButtonText: 'Yes, Enable',
          cancelButtonText: 'Cancel',
          ...confirmColors('danger'),
        });
        if (!result.isConfirmed) {
          // Explicit reset guards future refactors that might pre-optimistically flip state.
          handleInputChange('redemption_only', false);
          return;
        }
      }

      handleInputChange(name, newValue);
    } catch (err) {
      toast.error('Could not update setting. Please try again.');
    }
  }, [handleInputChange]);

  // --- CONFIG GENERATION ---
  const {
    basicInfo,
    associatedInfo,
    bankingInfo,
    tradingAccountInfo,
    custodyInfo,
    nomineeInfo,
    jointHolderInfo,
    contactPersonInfo,
    tradingSettings,
    auditInfo
  } = useMemo(() => {
    return getClientFieldConfigs({
      schemes: schemes.map(s => ({ id: s.id, name: s.name })),
      categories: categories.map(c => ({ id: c.id, name: c.name })),
      groups: groups.map(g => ({ id: g.id, name: g.group_name || g.name })),
      distributors: distributors.map(d => ({ id: d.id, name: d.name })),
      countries: countries.map(c => ({ id: c.id, name: c.name })),
      clientClasses: clientClasses.map(c => ({ id: c.id, name: c.name, class_isin: (c as any).class_isin }))
    });
  }, [schemes, categories, groups, distributors, countries, clientClasses]);

  // Sync validation rules: Extract static required fields from configs and specific dynamic rules
  useEffect(() => {
    const allFieldConfigs = [
      ...basicInfo,
      ...associatedInfo,
      ...bankingInfo,
      ...tradingAccountInfo,
      ...custodyInfo,
      ...nomineeInfo,
      ...jointHolderInfo,
      ...contactPersonInfo,
      ...tradingSettings
    ];

    const staticRequired = allFieldConfigs
      .filter((f: any) => f.required)
      .map((f: any) => f.name);

    // Merge static required fields with any dynamic ones from DB (requiredFields prop)
    // IMPORTANT: If we have dynamic fieldConfigs, we should only require fields that are actually visible
    const hasConfigs = fieldConfigs.length > 0;
    const configuredFieldNames = fieldConfigs.map((c: any) => c.field_name);

    const filteredStaticRequired = hasConfigs
      ? staticRequired.filter(name => configuredFieldNames.includes(name))
      : staticRequired;

    const combinedRequired = Array.from(new Set([...filteredStaticRequired, ...requiredFields]));

    setRequiredFields(combinedRequired);
  }, [basicInfo, associatedInfo, bankingInfo, tradingAccountInfo, custodyInfo, nomineeInfo, jointHolderInfo, contactPersonInfo, tradingSettings, requiredFields, fieldConfigs, setRequiredFields]);

  // Process fields
  const processFields = (fields: any[]) => {
    const configuredFieldNames = fieldConfigs.map((c: any) => c.field_name);
    const hasConfigs = fieldConfigs.length > 0;

    return fields
      .filter(f => {
        // If we have configurations from DB, only show fields that are in the list
        if (hasConfigs) {
          return configuredFieldNames.includes(f.name);
        }

        // Client Class is AIF-only; Group applies to both types.
        if (formClientType === 'PMS' && f.name === 'client_class') return false;
        if (f.name === 'restricted_companies') return false;
        return true;
      })
      .map(f => {
        const field = { ...f, required: f.required || isFieldRequired(f.name) };

        // Disable status fields on update
        if (clientId && (field.name === 'is_active' || field.name === 'legacy_client')) {
          field.disabled = true;
        }

        return field;
      });
  };

  const isSectionVisible = (fields: any[]) => {
    return processFields(fields).length > 0;
  };

  if (formDataLoading || supportingDataLoading) return <Placeholder kind="loading" />;
  if (supportingDataError) return <Note tone="error">{supportingDataError}</Note>;

  // Selection Screen
  if (!isTypeSelected && !clientId && !isApprovalMode) {
    return (
      <PageFrame header={<PageHeader title="Create New Client" />}>
        <Surface padded className={styles['client-type-card']}>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            Please select the client type you want to create.
          </Typography>

          <Box sx={{ mb: 4 }}>
            <Select
              fullWidth
              value={formClientType}
              onChange={(e) => {
                const val = e.target.value as 'PMS' | 'AIF';
                setFormClientType(val);
                setFormData((prev: any) => ({
                  ...prev,
                  client_type: val, // Sync form data
                  client_class: val === 'PMS' ? 0 : prev.client_class
                }));
              }}
            >
              <MenuItem value="PMS">PMS Client</MenuItem>
              <MenuItem value="AIF">AIF Client</MenuItem>
            </Select>
          </Box>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button onClick={() => navigate('/clients')}>
              Back to List
            </Button>
            <Button
              variant="primary"
              onClick={() => setIsTypeSelected(true)}
            >
              Continue to Form
            </Button>
          </Stack>
        </Surface>
      </PageFrame>
    );
  }

  const showRestrictedCompanies = !fieldConfigs.length || fieldConfigs.some((c: any) => c.field_name === 'restricted_companies');

  // Main Form
  const formTitle = `${clientId ? (viewMode ? `View Client (${formData.client_code})` : `Edit Client (${formData.client_code})`) : 'New Client'} (${formClientType})`;

  return (
    <PageFrame header={<PageHeader title={formTitle} />}>
    <Surface padded>
      <Box sx={{
        py: 1.5,
        borderBottom: '1px solid var(--border-soft)',
        // Bleed to the Surface card's edges, then re-pad to its content width.
        marginTop: 'calc(var(--card-pad) * -1)',
        marginLeft: 'calc(var(--card-pad) * -1)',
        marginRight: 'calc(var(--card-pad) * -1)',
        paddingLeft: 'var(--card-pad)',
        paddingRight: 'var(--card-pad)',
      }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
          <TabBar
            ariaLabel="Client form section"
            value={activeTab}
            onChange={(v) => {
              setActiveTab(v as 'info' | 'fees');
              if (v === 'fees') setFeeTabVisited(true);
            }}
            tabs={[
              { value: 'info', label: 'Client Information' },
              { value: 'fees', label: 'Fee Structure & Contributions' },
            ]}
          />
          <Stack direction="row" spacing={2}>
            {isApprovalMode ? (
              <Button onClick={() => navigate('/client-approvals')}>
                Back to Approvals
              </Button>
            ) : viewMode ? (
              <>
                <Button onClick={() => navigate('/clients')}>
                  Back to Client List
                </Button>
                {!!clientId && (
                  <Button
                    onClick={() => navigate(`/clientwise-performance?client_id=${clientId}`)}
                  >
                    View Performance
                  </Button>
                )}
                {!!clientId && (
                  <ScopeGate scope={[READ_CLIENT_MASTER, WRITE_CLIENT_MASTER]}>
                    <Button
                      onClick={handleSyncToZoho}
                      disabled={isSyncingToZoho}
                    >
                      {isSyncingToZoho ? 'Syncing…' : 'Sync to Zoho'}
                    </Button>
                  </ScopeGate>
                )}
                <Button
                  variant="primary"
                  onClick={() => {
                    const path = `/client-master/${clientId}/edit`;
                    navigate(path);
                  }}
                >
                  Edit Client
                </Button>
              </>
            ) : !isApprovalMode && (
              <>
                <Button onClick={() => navigate('/clients')}>
                  Back to Client List
                </Button>
                <Button
                  onClick={() => {
                    if (clientId) {
                      const path = `/client-master/${clientId}`;
                      navigate(path);
                    } else {
                      navigate('/client-master');
                    }
                  }}
                >
                  Cancel
                </Button>
                <Button variant="primary" onClick={handleSave} disabled={formSaving}>
                  {formSaving ? 'Saving...' : (clientId ? 'Update Client' : 'Create Client')}
                </Button>
              </>
            )}
          </Stack>
        </Stack>
      </Box>

      {isApprovalMode && approvalDetails?.action_type && (
        <Box sx={{ mt: 2, mb: 1 }}>
          <Note tone={approvalDetails.action_type === 'DEACTIVATE' ? 'warning' : approvalDetails.action_type === 'ACTIVATE' ? 'success' : 'info'}>
            {approvalDetails.action_type === 'ACTIVATE' && <strong>Activation Request</strong>}
            {approvalDetails.action_type === 'DEACTIVATE' && <strong>Deactivation Request</strong>}
            {approvalDetails.action_type === 'CREATE' && <strong>New Client Request</strong>}
            {approvalDetails.action_type === 'UPDATE' && <strong>Update Request</strong>}
          </Note>
        </Box>
      )}

      {isApprovalMode && approvalDetails?.change_reason && (
        <Box sx={{ mt: 1, mb: 1 }}>
          <Note tone="info">
            <strong>Change Reason:</strong> {approvalDetails.change_reason}
          </Note>
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: activeTab === 'info' ? 'block' : 'none' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {isSectionVisible(basicInfo) && (
              <ClientInfoSection
                title="Basic Information"
                fields={processFields(basicInfo)}
                data={formData}
                mode={viewMode || isApprovalMode ? 'view' : 'edit'}
                onChange={(e) => {
                  const { name, value, type, checked } = e.target as any;
                  handleInputChange(name, type === 'checkbox' ? checked : value);
                }}
                errors={validationErrors}
                loading={formSaving}
                changedFields={changedFields}
                requiredFields={requiredFields}
                renderPreviousValue={renderPreviousValue}
              />
            )}

            {isSectionVisible(associatedInfo) && (
              <ClientInfoSection
                title="Associated Information"
                fields={processFields(associatedInfo)}
                data={formData}
                mode={viewMode || isApprovalMode ? 'view' : 'edit'}
                onChange={(e) => {
                  const { name, value, type, checked } = e.target as any;
                  handleInputChange(name, type === 'checkbox' ? checked : value);
                }}
                errors={validationErrors}
                loading={formSaving}
                changedFields={changedFields}
                renderPreviousValue={renderPreviousValue}
              />
            )}

            {/* Manual Restricted Companies Row */}
            {showRestrictedCompanies && (
              <div className={commonStyles['client-common-section']}>
                <h3>Restricted Companies</h3>
                <div className={commonStyles['client-common-grid']}>
                  <div className={`${commonStyles['client-common-field']} ${commonStyles['full-width']}`}>
                    {viewMode || isApprovalMode ? (
                      <>
                        <label>Selected Companies</label>
                        <div className={`${commonStyles['view-value']} ${changedFields['restricted_companies'] ? commonStyles['changed'] : ''}`}>
                          {formData.restricted_companies && formData.restricted_companies.length > 0
                            ? mappedCompanyOptions
                              .filter(opt => (formData.restricted_companies || []).includes(opt.id))
                              .map(opt => opt.label)
                              .join(', ')
                            : '—'}
                          {changedFields['restricted_companies'] && renderPreviousValue && renderPreviousValue('restricted_companies')}
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          style={changedFields['restricted_companies']
                            ? { border: '1px solid var(--warn-line)', background: 'var(--warn-soft)', borderRadius: 6, padding: 4 }
                            : undefined}
                        >
                          <FilterAsyncMultiSelect
                            label="Search and Select Companies"
                            value={selectedCompanyOptions}
                            onChange={(next) => {
                              const picked = next.map(opt => ({ id: Number(opt.value), label: opt.label }));
                              setCompanyOptions(prev => {
                                const known = new Set(prev.map(o => o.id));
                                const added = picked.filter(o => !known.has(o.id));
                                return added.length ? [...prev, ...added] : prev;
                              });
                              handleInputChange('restricted_companies', picked.map(o => o.id));
                            }}
                            loadOptions={fetchCompanies}
                            limitTags={5}
                          />
                        </div>
                        {changedFields['restricted_companies'] && renderPreviousValue && renderPreviousValue('restricted_companies')}
                        {validationErrors['restricted_companies'] && (
                          <span className={commonStyles['client-field-error']}>{validationErrors['restricted_companies']}</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {clientId && formData.client_type === 'AIF' && (
              <ClientSeriesField
                key={clientId}
                clientId={clientId}
                mode={isApprovalMode ? 'approval' : viewMode ? 'view' : 'edit'}
                previousSeries={isApprovalMode ? approvalDetails?.data?.clientseriesmapping : undefined}
                onChange={setSeriesRefs}
                disabled={formSaving}
              />
            )}

            {isSectionVisible(bankingInfo) && (
              <ClientInfoSection
                title="Banking Information"
                fields={processFields(bankingInfo)}
                data={formData}
                mode={viewMode || isApprovalMode ? 'view' : 'edit'}
                onChange={(e) => {
                  const { name, value } = e.target;
                  handleInputChange(name, value);
                }}
                errors={validationErrors}
                loading={formSaving}
                changedFields={changedFields}
                renderPreviousValue={renderPreviousValue}
              />
            )}

            {!!formData.is_nri && isSectionVisible(tradingAccountInfo) && (
              <ClientInfoSection
                title="Trading Account"
                fields={processFields(tradingAccountInfo)}
                data={formData}
                mode={viewMode || isApprovalMode ? 'view' : 'edit'}
                onChange={(e) => {
                  const { name, value } = e.target;
                  handleInputChange(name, value);
                }}
                errors={validationErrors}
                loading={formSaving}
                changedFields={changedFields}
                renderPreviousValue={renderPreviousValue}
              />
            )}

            {isSectionVisible(custodyInfo) && (
              <ClientInfoSection
                title="Custody Information"
                fields={processFields(custodyInfo)}
                data={formData}
                mode={viewMode || isApprovalMode ? 'view' : 'edit'}
                onChange={(e) => {
                  const { name, value } = e.target;
                  handleInputChange(name, value);
                }}
                errors={validationErrors}
                loading={formSaving}
                changedFields={changedFields}
                renderPreviousValue={renderPreviousValue}
              />
            )}

            {isSectionVisible(nomineeInfo) && (
              <ClientInfoSection
                title="Nominee Information"
                fields={processFields(nomineeInfo)}
                data={formData}
                mode={viewMode || isApprovalMode ? 'view' : 'edit'}
                onChange={(e) => {
                  const { name, value } = e.target;
                  handleInputChange(name, value);
                }}
                errors={validationErrors}
                loading={formSaving}
                changedFields={changedFields}
                renderPreviousValue={renderPreviousValue}
              />
            )}

            {isSectionVisible(jointHolderInfo) && (
              <ClientInfoSection
                title="Joint Holder Information"
                fields={processFields(jointHolderInfo)}
                data={formData}
                mode={viewMode || isApprovalMode ? 'view' : 'edit'}
                onChange={(e) => {
                  const { name, value } = e.target;
                  handleInputChange(name, value);
                }}
                errors={validationErrors}
                loading={formSaving}
                changedFields={changedFields}
                renderPreviousValue={renderPreviousValue}
              />
            )}

            {isSectionVisible(contactPersonInfo) && (
              <ClientInfoSection
                title="Contact Person & Beneficial Ownership"
                fields={processFields(contactPersonInfo)}
                data={formData}
                mode={viewMode || isApprovalMode ? 'view' : 'edit'}
                onChange={(e) => {
                  const { name, value } = e.target;
                  handleInputChange(name, value);
                }}
                errors={validationErrors}
                loading={formSaving}
                changedFields={changedFields}
                renderPreviousValue={renderPreviousValue}
              />
            )}

            {isSectionVisible(tradingSettings) && (
              <ClientInfoSection
                title="Trading Settings"
                fields={processFields(tradingSettings)}
                data={formData}
                mode={viewMode || isApprovalMode ? 'view' : 'edit'}
                onChange={handleTradingSettingsChange}
                errors={validationErrors}
                loading={formSaving}
                changedFields={changedFields}
                renderPreviousValue={renderPreviousValue}
              />
            )}

            {clientId && isSectionVisible(auditInfo) && (
              <ClientInfoSection
                title="Audit Information"
                fields={processFields(auditInfo)}
                data={formData}
                mode="view"
              />
            )}
          </Box>
        </Box>

        {feeTabVisited && (
        <Box sx={{ display: activeTab === 'fees' ? 'block' : 'none' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <FeeStructure
              key={`fee-${routeId ?? 'new'}-${viewMode || isApprovalMode}`}
              ref={feeStructureRef}
              client_id={clientId || undefined}
              client_code={formData.client_code}
              scheme_id={Number(formData.scheme)}
              viewMode={viewMode || isApprovalMode}
              previousFeeComponents={approvalDetails?.data?.clientfeecomponent}
              data={approvalDetails?.object_details?.clientfeecomponent || approvalDetails?.object_details?.fee_components}
            />

            <Contributions
              key={`contrib-${routeId ?? 'new'}-${viewMode || isApprovalMode}`}
              ref={contributionsRef}
              client_id={clientId || undefined}
              client_code={formData.client_code}
              scheme_id={Number(formData.scheme)}
              viewMode={viewMode || isApprovalMode}
              previousContributions={approvalDetails?.data?.contribution}
              data={approvalDetails?.object_details?.clientcontribution || approvalDetails?.object_details?.contributions}
            />

            {viewMode && !isApprovalMode && (
              <FeeNotes
                client_id={clientId || undefined}
              />
            )}
          </Box>
        </Box>
        )}
      </Box>
    </Surface>

    {isApprovalMode && approvalDetails && (
      <ApprovalActions
        approvalId={approvalId!}
        onApprove={() => navigate('/client-approvals')}
        onReject={() => navigate('/client-approvals')}
      />
    )}
    </PageFrame>
  );
};

export default ClientForm;
